<template>
  <div
    class="homepage-carousel relative"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Carousel Container -->
    <div class="carousel-container relative rounded-none lg:rounded-lg">
      <div
        class="carousel-track flex transition-transform duration-300 ease-in-out"
        :style="{ transform: `translateX(-${currentSlide * 100}%)` }"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <!-- Featured articles slides -->
        <div
          v-for="(article, index) in featuredArticles"
          :key="`slide-${index}`"
          :class="[
            'carousel-slide w-full flex-shrink-0 transition-opacity duration-300 ease-in-out',
            { 'active-slide': index === currentSlide },
          ]"
          :style="{ opacity: getSlideOpacity(index) }"
        >
          <CarouselSlide
            :article="article"
            :index="index"
            :is-active="index === currentSlide"
            @activate="goToSlide"
          />
        </div>
      </div>
    </div>

    <!-- Dot Indicators -->
    <CarouselDots
      :total-slides="featuredArticles.length"
      :current-slide="currentSlide"
      @go-to-slide="goToSlide"
    />

    <!-- Fallback Message -->
    <div v-if="featuredArticles.length === 0" class="text-center py-12">
      <p class="text-gray-500">No featured articles available.</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CarouselSlide from './CarouselSlide.vue';
import CarouselDots from './CarouselDots.vue';
import type { CarouselArticle } from '~/types/carousel-article';

interface Props {
  articles?: CarouselArticle[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

const props = withDefaults(defineProps<Props>(), {
  articles: () => [],
  autoPlay: false,
  autoPlayInterval: 5000,
});

// Filter featured articles (by featured flag)
const featuredArticles = computed(() =>
  props.articles.filter((article) => article.featured === true)
);

// Calculate opacity for each slide based on position
const getSlideOpacity = (index: number) => {
  return index === currentSlide.value ? 1 : 0.5;
};

// Carousel state
const currentSlide = ref(0);
const isAutoPlaying = ref(props.autoPlay);
let autoPlayTimer: NodeJS.Timeout | null = null;
const prefersReducedMotion = ref(false);

// Touch handling for mobile
const touchStartX = ref(0);
const touchEndX = ref(0);

// Navigation methods with rewind behavior
const nextSlide = () => {
  const totalSlides = featuredArticles.value.length;
  if (totalSlides > 0) {
    currentSlide.value = (currentSlide.value + 1) % totalSlides;
  }
};

const previousSlide = () => {
  const totalSlides = featuredArticles.value.length;
  if (totalSlides > 0) {
    currentSlide.value = currentSlide.value <= 0 ? totalSlides - 1 : currentSlide.value - 1;
  }
};

const goToSlide = (index: number) => {
  currentSlide.value = index;
  resetAutoPlay();
};

// Auto-play functionality
const startAutoPlay = () => {
  if (props.autoPlay && featuredArticles.value.length > 1 && !prefersReducedMotion.value) {
    autoPlayTimer = setInterval(() => {
      if (isAutoPlaying.value) {
        nextSlide();
      }
    }, props.autoPlayInterval);
  }
};

const stopAutoPlay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

// Touch event handlers
const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX;
  isAutoPlaying.value = false; // Pause auto-play during touch
};

const handleTouchMove = (e: TouchEvent) => {
  touchEndX.value = e.touches[0].clientX;
};

const handleTouchEnd = () => {
  const touchDiff = touchStartX.value - touchEndX.value;
  const minSwipeDistance = 50;

  if (Math.abs(touchDiff) > minSwipeDistance) {
    if (touchDiff > 0) {
      nextSlide(); // Swipe left - next slide
    } else {
      previousSlide(); // Swipe right - previous slide
    }
  }

  isAutoPlaying.value = props.autoPlay; // Resume auto-play
  resetAutoPlay();
};

// Lifecycle
onMounted(() => {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  prefersReducedMotion.value = mediaQuery.matches;
  mediaQuery.addEventListener('change', (e) => {
    prefersReducedMotion.value = e.matches;
    if (e.matches) {
      stopAutoPlay();
    } else {
      startAutoPlay();
    }
  });
  startAutoPlay();
});

onUnmounted(() => {
  stopAutoPlay();
});

// Pause auto-play on hover (desktop)
const handleMouseEnter = () => {
  isAutoPlaying.value = false;
};

const handleMouseLeave = () => {
  isAutoPlaying.value = props.autoPlay;
};

// Watch for articles changes
watch(
  () => props.articles,
  () => {
    currentSlide.value = 0; // Reset to first slide
    resetAutoPlay();
  }
);
</script>
